// src/components/AdminTopMenuBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const AdminTopMenuBar = () => {
  const location = useLocation();
  const [managementOpen, setManagementOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setManagementOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Primary menu items (always visible)
  const primaryItems = [
    { label: 'Control Panel', path: '/admin' },
    { label: 'Dashboard', path: '/admin/dashboard' },
    { label: 'Users', path: '/admin/users' },
    { label: 'Books', path: '/admin/books' },
    { label: 'Authors', path: '/admin/authors' },
    { label: 'Publishers', path: '/admin/publishers' },
  ];

  // Management submenu items
  const managementItems = [
    { label: 'Marketing', path: '/admin/marketing' },
    { label: 'Referrals', path: '/admin/referrals' },
    { label: 'Engagement', path: '/admin/engagement' },
    { label: 'Pricing', path: '/admin/pricing' },
    { label: 'Credits', path: '/admin/credits' },
    { label: 'ECC', path: '/admin/economics' },
    { label: 'System', path: '/admin/system' },
    { label: 'Diagnostics', path: '/admin/diagnostics' },
    { label: 'Housekeeping', path: '/admin/housekeeping' },
    { label: 'Event Kiosk', path: '/admin/kiosk' },
  ];

  // Check if current path is in management section
  const isInManagement = managementItems.some(item => 
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  // Filter out the current page link (smart hiding)
  const visiblePrimaryItems = primaryItems.filter(item => {
    if (item.path === '/admin' && location.pathname === '/admin') {
      return false;
    }
    if (item.path !== '/admin' && location.pathname.startsWith(item.path)) {
      return false;
    }
    return true;
  });

  return (
    <div className="sticky top-[4.5rem] md:top-[5.5rem] z-40 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
      <div className="mx-auto max-w-7xl px-3">
        <div className="flex flex-wrap justify-center items-center gap-2 py-2">
          {visiblePrimaryItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="rounded-full px-4 py-1.5 text-sm font-bold text-white hover:bg-amber-900/30 hover:text-amber-300 transition-colors"
            >
              {item.label}
            </Link>
          ))}
          
          {/* Management Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setManagementOpen(!managementOpen)}
              className={`flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${
                isInManagement 
                  ? 'bg-amber-900/50 text-amber-300' 
                  : 'text-white hover:bg-amber-900/30 hover:text-amber-300'
              }`}
            >
              Management
              <ChevronDown className={`w-4 h-4 transition-transform ${managementOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {managementOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 rounded-lg border border-slate-700 bg-slate-900 py-2 shadow-xl z-50">
                {managementItems.map((item) => {
                  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setManagementOpen(false)}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-amber-900/30 text-amber-300'
                          : 'text-slate-200 hover:bg-slate-800 hover:text-amber-300'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTopMenuBar;
